import React from 'react';
import { ArrowRight, Link as LinkIcon } from '@phosphor-icons/react';
import { formatCurrency } from '../../utils/formatters';
import { VAT_RATE } from '../../utils/costCalculations';
import tableStyles from '../../styles/tables.module.css';
import pageStyles from '../../styles/pages.module.css';
import styles from './QuoteDetailView.module.css';

// Format price as whole-number currency with dash for null
const formatPrice = amount => (amount == null ? '—' : formatCurrency(amount));

/**
 * QuoteDetailView — shared layout for viewing a single vendor's priced quote.
 *
 * Handles the outer chrome (header, vendor summary, items table, totals).
 * Consumers pre-shape their data into `groups` and `totals`.
 *
 * Props:
 * - vendorName / vendorContact — vendor display info
 * - groups: Array<{
 *     id: string,
 *     headerLabel: string,       // pre-formatted group header (e.g. "01 - חפירה")
 *     groupTotal?: number,       // optional inline total shown in header
 *     items: Array<{
 *       id, name, linePrice, lineTotal, lineNotes, isSkipped,
 *       unitLabel?, quantity?                 // only used if showUnitAndQty
 *     }>
 *   }>
 * - totals: { sum: number, pricedCount: number, skippedCount: number, totalItems: number }
 * - onBack: () => void
 * - onShare?: () => void           // omits share button if not provided
 * - showUnitAndQty: boolean        // true for BOQ-style columns; false for simple list
 * - backLabel / shareLabel — customizable button text
 */
const QuoteDetailView = ({
  vendorName,
  vendorContact,
  groups = [],
  totals = { sum: 0, pricedCount: 0, skippedCount: 0, totalItems: 0 },
  onBack,
  onShare,
  showUnitAndQty = true,
  backLabel = 'חזרה לרשימה',
  shareLabel = 'העתק קישור לספק',
}) => {
  const totalColumnCount = showUnitAndQty ? 7 : 5;

  return (
    <div className={styles.container}>
      {/* Header with back button */}
      <div className={styles.header}>
        {onBack && (
          <button className={styles.backButton} onClick={onBack} type="button">
            <ArrowRight size={20} />
            {backLabel}
          </button>
        )}
        {onShare && (
          <div className={styles.headerRight}>
            <button className={pageStyles.secondaryButton} onClick={onShare} type="button">
              <LinkIcon size={18} />
              {shareLabel}
            </button>
          </div>
        )}
      </div>

      {/* Vendor + Summary Card */}
      <div className={pageStyles.card}>
        <div className={styles.summarySection}>
          <div className={styles.vendorInfo}>
            <h2 className={styles.vendorTitle}>{vendorName || 'ספק'}</h2>
            {vendorContact && <span className={styles.vendorContact}>{vendorContact}</span>}
          </div>
          <div className={styles.summaryStats}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>סה&quot;כ</span>
              <span className={styles.statValue}>{formatPrice(totals.sum)}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>כולל מע&quot;מ</span>
              <span className={styles.statValue}>{formatPrice(totals.sum * (1 + VAT_RATE))}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>תומחרו</span>
              <span className={styles.statValue}>
                {totals.pricedCount}/{totals.totalItems}
              </span>
            </div>
            {totals.skippedCount > 0 && (
              <div className={styles.statItem}>
                <span className={styles.statLabel}>דילוגים</span>
                <span className={styles.statValueMuted}>{totals.skippedCount}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Items table grouped by group header */}
      <div className={tableStyles.tableContainer}>
        <div className={tableStyles.tableScrollWrapper}>
          <table className={tableStyles.table}>
            <thead>
              <tr className={tableStyles.headerRow}>
                <th className={tableStyles.headerCell} style={{ width: '60px' }}>
                  #
                </th>
                <th className={tableStyles.headerCell}>פריט</th>
                {showUnitAndQty && (
                  <>
                    <th className={tableStyles.headerCell} style={{ width: '80px' }}>
                      יחידה
                    </th>
                    <th className={tableStyles.headerCell} style={{ width: '60px' }}>
                      כמות
                    </th>
                  </>
                )}
                <th className={tableStyles.headerCell} style={{ width: '120px' }}>
                  מחיר ספק
                </th>
                <th className={tableStyles.headerCell} style={{ width: '120px' }}>
                  סה&quot;כ שורה
                </th>
                <th className={tableStyles.headerCell} style={{ minWidth: '150px' }}>
                  הערות ספק
                </th>
              </tr>
            </thead>
            <tbody>
              {groups.map(group => {
                let rowIndex = 0;
                return (
                  <React.Fragment key={group.id}>
                    {/* Group header */}
                    <tr className={tableStyles.categoryHeader}>
                      <td colSpan={totalColumnCount}>
                        {group.headerLabel}
                        {group.groupTotal > 0 && (
                          <span style={{ color: '#64748b', fontWeight: 500, marginRight: '8px' }}>
                            (סה&quot;כ {formatPrice(group.groupTotal)})
                          </span>
                        )}
                      </td>
                    </tr>

                    {/* Items in group */}
                    {group.items.map(item => {
                      rowIndex++;
                      return (
                        <tr
                          key={item.id}
                          className={`${tableStyles.row} ${item.isSkipped ? styles.skippedRow : ''}`}
                        >
                          <td className={`${tableStyles.cell} ${tableStyles.cellSecondary}`}>
                            {rowIndex}
                          </td>
                          <td className={tableStyles.cell} style={{ fontWeight: 500 }}>
                            {item.name}
                          </td>
                          {showUnitAndQty && (
                            <>
                              <td className={tableStyles.cell}>{item.unitLabel || '—'}</td>
                              <td className={tableStyles.cell} style={{ textAlign: 'center' }}>
                                {item.quantity != null ? item.quantity : '—'}
                              </td>
                            </>
                          )}
                          <td className={tableStyles.cell}>
                            {item.isSkipped ? (
                              <span className={styles.skippedLabel}>דילוג</span>
                            ) : (
                              <span className={styles.vendorPrice}>
                                {formatPrice(item.linePrice)}
                              </span>
                            )}
                          </td>
                          <td className={tableStyles.cell}>
                            {item.isSkipped ? (
                              '—'
                            ) : (
                              <span style={{ fontWeight: 600 }}>
                                {item.lineTotal != null ? formatPrice(item.lineTotal) : '—'}
                              </span>
                            )}
                          </td>
                          <td
                            className={`${tableStyles.cell} ${tableStyles.cellSecondary}`}
                            style={{ fontSize: '13px' }}
                          >
                            {item.lineNotes || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Totals section */}
      {totals.sum > 0 && (
        <div className={pageStyles.card}>
          <div className={styles.totalsSection}>
            <div className={styles.totalRow}>
              <span>סה&quot;כ לפני מע&quot;מ</span>
              <span className={styles.totalAmount}>{formatCurrency(totals.sum)}</span>
            </div>
            <div className={styles.totalRow}>
              <span>מע&quot;מ ({(VAT_RATE * 100).toFixed(0)}%)</span>
              <span className={styles.totalAmount}>{formatCurrency(totals.sum * VAT_RATE)}</span>
            </div>
            <div className={`${styles.totalRow} ${styles.grandTotalRow}`}>
              <span>סה&quot;כ כולל מע&quot;מ</span>
              <span className={styles.totalAmount}>
                {formatCurrency(totals.sum * (1 + VAT_RATE))}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetailView;
